import ICAL from "ical.js";
import houses from "../../pages/HouseInfo";
import { getIcsText } from "../../api/calendar";
import { getProperties } from "../../api/property";

export type House = (typeof houses)[number];

/** A booked span, half open: [start, end). The end date is a check-out day,
 *  so it is still bookable as the next guest's check-in. */
export type DateRange = {
  start: Date;
  end: Date;
};

export type HouseAvailability = {
  house: House;
  blocked: DateRange[];
  /** true when at least one calendar feed could not be read */
  hadError: boolean;
};

export function startOfDay(date: Date): Date {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
}

export function addDays(date: Date, days: number): Date {
  const day = startOfDay(date);
  day.setDate(day.getDate() + days);
  return day;
}

export function nightsBetween(start: Date, end: Date): number {
  const ms = startOfDay(end).getTime() - startOfDay(start).getTime();
  // rounding keeps DST transitions (23h / 25h days) from shifting the count
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
}

export function rangesOverlap(a: DateRange, b: DateRange): boolean {
  return a.start < b.end && b.start < a.end;
}

export function isAvailable(
  availability: HouseAvailability,
  stay: DateRange,
): boolean {
  return !availability.blocked.some((booked) => rangesOverlap(stay, booked));
}

/** The stay a selection represents. A lone date means a single night. */
export function stayFromSelection(
  checkIn: Date,
  checkOut: Date | null,
): DateRange {
  const start = startOfDay(checkIn);
  const end = checkOut ? startOfDay(checkOut) : addDays(start, 1);
  return { start, end: end > start ? end : addDays(start, 1) };
}

/** Mongo hands back UTC midnights; read those as calendar days so a booking
 *  does not slide a day backwards in a western timezone. */
function toLocalDay(value: string | Date): Date {
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return parsed;

  if (
    parsed.getUTCHours() === 0 &&
    parsed.getUTCMinutes() === 0 &&
    parsed.getUTCSeconds() === 0
  ) {
    return new Date(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
    );
  }
  return startOfDay(parsed);
}

function parseIcsToRanges(icsText: string): DateRange[] {
  const component = new ICAL.Component(ICAL.parse(icsText));

  return component
    .getAllSubcomponents("vevent")
    .map((vevent: any): DateRange | null => {
      try {
        const event = new ICAL.Event(vevent);
        const start = startOfDay(event.startDate.toJSDate());
        const end = startOfDay(event.endDate.toJSDate());
        if (isNaN(start.getTime())) return null;
        return { start, end: end > start ? end : addDays(start, 1) };
      } catch (error) {
        return null;
      }
    })
    .filter((range: DateRange | null): range is DateRange => range !== null);
}

function dedupe(ranges: DateRange[]): DateRange[] {
  const seen = new Set<string>();
  return ranges.filter((range) => {
    const key = `${range.start.getTime()}__${range.end.getTime()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Bookings taken through this site, keyed by the property id in HouseInfo. */
async function loadBookingsFromDb(): Promise<Map<string, DateRange[]>> {
  const byPropertyId = new Map<string, DateRange[]>();

  try {
    const properties = await getProperties();

    (properties ?? []).forEach((property: any) => {
      if (!property?.propertyId) return;

      const ranges = (property.bookings ?? [])
        .filter((booking: any) => booking?.startDate && booking?.endDate)
        .map((booking: any) => ({
          start: toLocalDay(booking.startDate),
          end: toLocalDay(booking.endDate),
        }))
        .filter(
          (range: DateRange) =>
            !isNaN(range.start.getTime()) &&
            !isNaN(range.end.getTime()) &&
            range.end > range.start,
        );

      byPropertyId.set(String(property.propertyId).trim().toUpperCase(), ranges);
    });
  } catch (error) {
    // The listing calendars are the source of truth; site bookings are a bonus.
    console.warn("Could not load bookings from the database", error);
  }

  return byPropertyId;
}

export async function loadAvailability(): Promise<HouseAvailability[]> {
  const bookingsByPropertyId = await loadBookingsFromDb();

  return Promise.all(
    houses.map(async (house): Promise<HouseAvailability> => {
      const urls = Array.from(
        new Set((house.icsUrls ?? []).filter(Boolean)),
      ) as string[];

      const responses = await Promise.all(urls.map((url) => getIcsText(url)));

      let hadError = false;
      const blocked: DateRange[] = [];

      responses.forEach((response) => {
        if (response.error || !response.data) {
          hadError = true;
          return;
        }
        try {
          blocked.push(...parseIcsToRanges(response.data));
        } catch (error) {
          hadError = true;
        }
      });

      blocked.push(
        ...(bookingsByPropertyId.get(house.propertyId.toUpperCase()) ?? []),
      );

      return { house, blocked: dedupe(blocked), hadError };
    }),
  );
}
